'use client';

import { Spinner } from '@/components/glitch-ui/spinner';
import { useRef, useState } from 'react';
import { Plus, Search, Tag } from 'lucide-react';
import { Command, CommandEmpty, CommandGroup, CommandItem, CommandList } from '@/components/ui/command';
import { cn } from '@/lib/utils';
import type { AdLabel } from '@/lib/types';

const LABEL_MAX_LENGTH = 63;

interface LabelComboboxProps {
  /** Selectable existing labels. Parent should filter out already-applied labels. */
  options: AdLabel[];
  /** Called with the chosen/created label name. Existing and new labels both add by name. */
  onAdd: (name: string) => void;
  isBusy?: boolean;
  placeholder?: string;
}

/**
 * Command-based combobox for adding ad labels: search the existing account labels
 * or create a new one inline. Emulates the LandingPageCombobox pattern (search +
 * inline "create new"), adapted for labels (multi-add, no URL validation, 63-char limit).
 */
export function LabelCombobox({
  options,
  onAdd,
  isBusy = false,
  placeholder = 'Search or create a label...',
}: LabelComboboxProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const trimmed = query.trim();
  const tooLong = trimmed.length > LABEL_MAX_LENGTH;

  const filtered = trimmed
    ? options.filter((o) => o.name.toLowerCase().includes(trimmed.toLowerCase()))
    : options;

  const exactMatch = options.find((o) => o.name.toLowerCase() === trimmed.toLowerCase());
  const showCreate = !!trimmed && !exactMatch && !tooLong;
  const hasResults = filtered.length > 0 || showCreate;

  const add = (name: string) => {
    const value = name.trim();
    if (!value || value.length > LABEL_MAX_LENGTH || isBusy) return;
    onAdd(value);
    setQuery('');
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  return (
    <div
      ref={containerRef}
      className="relative w-full"
      onBlur={(e) => {
        if (!containerRef.current?.contains(e.relatedTarget as Node)) setOpen(false);
      }}
    >
      <div
        className={cn(
          'flex items-center min-h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background',
          'focus-within:outline-none focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2',
          isBusy && 'opacity-50',
        )}
        onClick={() => {
          inputRef.current?.focus();
          setOpen(true);
        }}
      >
        {isBusy ? (
          <Spinner size="sm" className="text-gray-400 flex-shrink-0" />
        ) : (
          <Search className="h-4 w-4 text-gray-400 flex-shrink-0" />
        )}
        <input
          ref={inputRef}
          value={query}
          placeholder={placeholder}
          disabled={isBusy}
          className="flex-1 bg-transparent outline-none placeholder:text-muted-foreground ml-2"
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={(e) => {
            if (e.key === 'Escape') setOpen(false);
            if (e.key === 'Enter') {
              e.preventDefault();
              if (exactMatch) add(exactMatch.name);
              else if (showCreate) add(trimmed);
            }
          }}
        />
      </div>

      {tooLong && (
        <p className="text-sm text-[#B91C1C] mt-1">Labels must be {LABEL_MAX_LENGTH} characters or fewer.</p>
      )}

      {open && (
        <div className="absolute left-0 right-0 top-full mt-1 z-50 bg-white border border-gray-200 rounded-lg shadow-lg">
          <Command shouldFilter={false}>
            <CommandList className="max-h-64">
              {!hasResults && (
                <CommandEmpty className="py-4 text-center text-sm text-gray-500">
                  {trimmed ? 'No matching labels.' : 'No labels yet.'}
                </CommandEmpty>
              )}

              {filtered.length > 0 && (
                <CommandGroup>
                  {filtered.map((label) => (
                    <CommandItem
                      key={label.id}
                      value={label.id}
                      onSelect={() => add(label.name)}
                      className="flex items-center gap-2 cursor-pointer"
                    >
                      <div className="flex-shrink-0 w-7 h-7 rounded-md bg-gray-100 flex items-center justify-center">
                        <Tag className="h-3.5 w-3.5 text-gray-500" />
                      </div>
                      <span className="text-sm font-medium text-gray-900 block truncate">{label.name}</span>
                    </CommandItem>
                  ))}
                </CommandGroup>
              )}

              {showCreate && (
                <CommandGroup heading="Create New">
                  <CommandItem
                    value="__create__"
                    onSelect={() => add(trimmed)}
                    className="flex items-center gap-2 cursor-pointer"
                  >
                    <div className="flex-shrink-0 w-7 h-7 rounded-md flex items-center justify-center bg-[#EDE9FE]">
                      <Plus className="h-3.5 w-3.5 text-[#290BD9]" />
                    </div>
                    <span className="text-sm font-medium text-[#290BD9] block truncate">Create &quot;{trimmed}&quot;</span>
                  </CommandItem>
                </CommandGroup>
              )}
            </CommandList>
          </Command>
        </div>
      )}
    </div>
  );
}
